// Screen shake, flashes, zoom punch, vignette. All scaled by speed, shake % and reduce-flashing.
import { Container, Graphics, Sprite, Texture, type Ticker } from 'pixi.js';
import { PALETTE, type PaletteKey } from '../../art/palette';
import { LOGICAL_H, LOGICAL_W, type GameApp } from '../app';
import { juice } from '../juice';
import { clock } from '../timeline/clock';

export class Fx {
  private shakeAmp = 0;
  private shakeDecay = 0;
  private flashRect: Sprite;
  private vignette: Graphics;
  private vignettePulse = 0;

  constructor(private ga: GameApp) {
    this.flashRect = new Sprite(Texture.WHITE);
    this.flashRect.width = LOGICAL_W;
    this.flashRect.height = LOGICAL_H;
    this.flashRect.alpha = 0;
    this.vignette = new Graphics();
    this.drawVignette();
    this.vignette.alpha = 0;
    ga.overlay.addChild(this.vignette, this.flashRect);
    ga.app.ticker.add(this.tick);
  }

  private drawVignette(): void {
    const g = this.vignette;
    const c = PALETTE.blood;
    for (let i = 0; i < 6; i++) {
      const inset = i * 4;
      g.rect(inset, inset, LOGICAL_W - inset * 2, LOGICAL_H - inset * 2).stroke({ color: c, width: 4, alpha: 0.35 - i * 0.05 });
    }
  }

  private tick = (t: Ticker) => {
    const w = this.ga.world;
    if (this.shakeAmp > 0.2) {
      const a = this.shakeAmp * juice.shake();
      w.x = Math.round((Math.random() * 2 - 1) * a);
      w.y = Math.round((Math.random() * 2 - 1) * a);
      this.shakeAmp *= Math.pow(this.shakeDecay, t.deltaMS / 16.67);
    } else if (w.x || w.y) {
      w.x = 0;
      w.y = 0;
      this.shakeAmp = 0;
    }
    if (this.vignettePulse > 0) {
      // ~1 Hz heartbeat; steady when reduce flashing is on.
      const beat = juice.reduceFlashing() ? 0.5 : 0.5 + 0.5 * Math.sin((performance.now() / 1000) * Math.PI * 2);
      this.vignette.alpha = this.vignettePulse * beat;
    }
  };

  /** Screen shake in logical px. Decays over roughly `ms`. */
  shake(px: number, ms = 300): void {
    if (clock.isSkipping || juice.shake() <= 0) return;
    this.shakeAmp = Math.max(this.shakeAmp, px);
    const frames = Math.max(2, clock.ms(ms) / 16.67);
    this.shakeDecay = Math.pow(0.05, 1 / frames);
  }

  /** White flash, or a soft colour fade when reduce-flashing is on. */
  flash(color: PaletteKey = 'white', strength = 0.8, ms = 80): void {
    if (clock.isSkipping) return;
    const reduce = juice.reduceFlashing();
    this.flashRect.tint = PALETTE[reduce && color === 'white' ? 'lavender' : color];
    this.flashRect.alpha = reduce ? Math.min(0.25, strength) : strength;
    clock.fx(this.flashRect, reduce ? 200 : ms, { alpha: 0, ease: 'power1.out' });
  }

  /** Cut to black and back (Devour/Consumed/It Wakes). */
  async blackout(inMs: number, holdMs: number, outMs: number): Promise<void> {
    this.flashRect.tint = PALETTE.ink;
    await clock.tween(this.flashRect, inMs, { alpha: 1 });
    await clock.wait(holdMs);
    await clock.tween(this.flashRect, outMs, { alpha: 0 });
  }

  async fadeTo(color: PaletteKey, alpha: number, ms: number): Promise<void> {
    this.flashRect.tint = PALETTE[color];
    await clock.tween(this.flashRect, ms, { alpha });
  }

  /** Zoom punch around the screen centre. */
  punch(target: Container, amount = 0.05, ms = 220): void {
    if (clock.isSkipping) return;
    const cx = LOGICAL_W / 2;
    const cy = LOGICAL_H / 2;
    target.pivot.set(cx, cy);
    target.position.set(cx, cy);
    target.scale.set(1 + amount);
    clock.fx(target.scale, ms, { x: 1, y: 1, ease: 'back.out(2)' });
  }

  /** Hunger vignette: pulses at RAVENOUS, stronger and red-hot at STARVING. */
  setVignette(on: boolean, strong = false): void {
    this.vignettePulse = on ? (strong ? 1 : 0.6) : 0;
    if (!on) this.vignette.alpha = 0;
  }
}
